// #region Imports
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { styled, type Theme } from "@mui/material/styles";
import {
  MRT_GlobalFilterTextField as GlobalFilterTextField,
  type MRT_ColumnSizingState,
  type MRT_RowData,
  type MRT_TableInstance,
  type MRT_TableOptions,
} from "material-react-table";
import { useCallback, useMemo, useState, type ReactElement, type ReactNode, type RefObject } from "react";
import { enumKeyOf } from "_shared/_utilities/enum-utils";
import TableAddActions, { type TableAddAction } from "_shared/_views/tables/table-add-actions";
import {
  applyColumnWidth,
  resizableColumnOptions,
} from "_shared/_views/tables/table-column-widths";
import {
  CollectionToolbar,
  CollectionToolbarSearch,
  CollectionToolbarViewButtons,
} from "_shared/_views/tables/table-toolbar";
import useMeasuredWidth from "_shared/_views/use-measured-width";
import { feeScheduleTableCellStyles } from "_shared/fee-schedules/categories/_views/fee-schedule-table-cell";
import { DocumentFileType } from "documents/document-file-type";
import { workPaneProseInset } from "work/_views/work-pane-layout";
import {
  DOCUMENT_COLLECTION_COLUMN_WIDTHS,
  fitDocumentCollectionColumns,
  type DocumentCollectionColumnFit,
} from "work/work-agreements/contracts/_views/document-collection-columns";
import type WorkDocument from "work/work-agreements/contracts/work-document";
import { paneStretchTable } from "_shared/_views/tables/table-pane-stretch";
import { usePersistedColumnVisibility } from "_shared/_views/tables/table-column-visibility-preference";
import { TableToolsMenu } from "_shared/_views/tables/table-tools-menu";
import { formatFullTimestamp, formatTimestamp, timestampSearchText } from "_shared/formatting/dates";
import { useMinuteClock } from "_shared/dates/use-minute-clock";
import SearchMatch from "_shared/_views/tables/search-match";
import { FeeScheduleTableCellText as AddedStampText } from "_shared/fee-schedules/categories/_views/fee-schedule-table-cell";
// #endregion

/**
 * The shape a proposal's document collection is drawn in — on a DRAFT and on an ACTIVE proposal alike.
 *
 * #4955 asked for both to read the way the Fee Schedule tab beside them does. The active proposal's
 * three collections (Conflict Documents, Client Policies, Vendor Policies) got there first, in
 * `document-redline-selection.tsx`; the draft's two (Your Conflicts Checks, Your Policies) were still a
 * MUI `List`. Rather than a second hand-rolled table — a THIRD rendering of a document collection —
 * everything the two have in common lives here: the columns a document has, how wide each one is, the
 * toolbar, the cell treatment, and the MRT options that make it a table rather than a list with lines
 * drawn on it — resizable columns included. The columns that do not fit are reached with the scrollbar
 * `MRTNoMemo` draws on the container; the chevrons that used to stand in for one are gone (#4955).
 *
 * What is NOT here is either surface's own cells. A redline row strikes a replaced name into its
 * replacement, colours what was added or removed, surrounds a decided-and-accepted document and pulses
 * the row the review is sitting on; a draft has no negotiation and therefore none of that. Those cells
 * stay in `document-redline-selection.tsx`, and the draft's plain ones in `selected-document-list.tsx`.
 * Synthesising redlines for a draft so it could share the cells too is what the event-negotiation
 * design forbids, so the SHAPE is shared and the cells are not.
 */

// #region Column identity
/**
 * The columns a document collection is made of.
 *
 * #5027 drew these collections as a table with ONE content column — an icon, a name and a caption
 * stacked inside it — which is a list wearing a table's clothes. The Fee Schedule beside them puts each
 * thing a category HAS in a column of its own, and that is what makes it read as a table. A document
 * has the same four: what it is called, what kind of file it is, who put it there, and when.
 */
export const DOCUMENT_COLUMN_ID = "document";
export const TYPE_COLUMN_ID = "type";
export const ADDED_BY_COLUMN_ID = "addedBy";
export const ADDED_COLUMN_ID = "added";
/** The leading lock column, drawn only for a reviewer who may not write to this collection. */
export const LOCKED_COLUMN_ID = "locked";
export const ROW_ACTIONS_COLUMN_ID = "mrt-row-actions";

export const DOCUMENT_COLUMN_HEADER = "Document";
export const TYPE_COLUMN_HEADER = "Type";
export const ADDED_BY_COLUMN_HEADER = "Added by";
export const ADDED_COLUMN_HEADER = "Added";

/**
 * The document's own columns, in the order they are read.
 *
 * The active page draws a redline decision ACROSS them and so has to know which is the row's leading
 * edge and which its trailing one; `feeScheduleTableCellStyles` keys its opt-outs off the same ids.
 */
export const DOCUMENT_CONTENT_COLUMN_IDS = [
  DOCUMENT_COLUMN_ID,
  TYPE_COLUMN_ID,
  ADDED_BY_COLUMN_ID,
  ADDED_COLUMN_ID,
];

// What a column asks for before anything has been measured, which is also what it is given whenever
// there is room for every column to have what it asked for. The numbers, the minimums underneath them
// and the order the columns are given up in all live in `document-collection-columns`; these are here
// because a column definition has to carry an MRT `size` whether or not the collection has been
// measured yet, and #4955's fix must change nothing on a first render.
export const LOCKED_COLUMN_WIDTH = DOCUMENT_COLLECTION_COLUMN_WIDTHS.locked.preferred;
export const DOCUMENT_COLUMN_WIDTH = DOCUMENT_COLLECTION_COLUMN_WIDTHS.document.preferred;
export const TYPE_COLUMN_WIDTH = DOCUMENT_COLLECTION_COLUMN_WIDTHS.type.preferred;
export const ADDED_BY_COLUMN_WIDTH = DOCUMENT_COLLECTION_COLUMN_WIDTHS.addedBy.preferred;
export const ADDED_COLUMN_WIDTH = DOCUMENT_COLLECTION_COLUMN_WIDTHS.added.preferred;

/** What a column says when the document it is drawing does not carry that value. */
export const UNKNOWN_VALUE = "Unknown";
// #endregion

// #region Row values
/** The file kind, as the extension a reader recognises. */
export function documentTypeLabel(document: WorkDocument | null | undefined): string {
  return enumKeyOf(DocumentFileType, document?.fileType).toLocaleUpperCase() || UNKNOWN_VALUE;
}

/**
 * When the document joined the proposal, as the Added column shows it: "2:15 PM" today, "Oct 9, 2:15 PM"
 * this year, "Mar 3, 2025" before. Pass the page's minute clock as `now`, so these cells and every
 * `TimestampCell` agree about which day today is. The short form drops the time of an earlier year, so
 * a cell that shows it also shows `documentCreatedFullLabel` (`DocumentAddedStamp`).
 */
export function documentCreatedLabel(document: WorkDocument | null | undefined, now?: Date): string {
  return formatTimestamp(document?.created, now) ?? UNKNOWN_VALUE;
}

/** The whole of when the document joined the proposal, to the minute: "Mar 3, 2025, 2:15 PM". */
export function documentCreatedFullLabel(document: WorkDocument | null | undefined): string {
  return formatFullTimestamp(document?.created) ?? UNKNOWN_VALUE;
}

/**
 * What the search reads for the Added column: every form the cell can draw, whatever day it is, so a
 * search for what the reader sees finds the row after midnight too (`timestampSearchText`).
 */
export function documentCreatedSearchText(document: WorkDocument | null | undefined): string {
  return timestampSearchText(document?.created) ?? UNKNOWN_VALUE;
}

/**
 * Orders two documents by when they joined the proposal, oldest first, for the Added column's sort.
 * Its text cannot be sorted: "Apr 1" would come before "Jan 1". A document with no stamp sorts first.
 */
export function compareDocumentsAdded(
  left: WorkDocument | null | undefined,
  right: WorkDocument | null | undefined,
): number {
  return (left?.created?.valueOf() ?? 0) - (right?.created?.valueOf() ?? 0);
}

/**
 * The Added cell: the short stamp, with the search marked in it, and the full stamp on hover (#6713).
 * It reads the shared minute clock, so yesterday's rows gain their date at midnight with the rest.
 */
export function DocumentAddedStamp<TData extends MRT_RowData>(
  props: Readonly<{ document: WorkDocument | null | undefined; table?: MRT_TableInstance<TData> }>,
) {
  // MRT's table instance is one mutable object, the same prop on every render, so a memoized
  // <SearchMatch table={table} /> would keep the search term it first marked (review of #6801).
  "use no memo";

  const now = useMinuteClock();
  return (
    <Tooltip title={documentCreatedFullLabel(props.document)} disableInteractive>
      <span>
        <AddedStampText>
          <SearchMatch table={props.table} text={documentCreatedLabel(props.document, now)} />
        </AddedStampText>
      </span>
    </Tooltip>
  );
}
// #endregion

// #region Collection shell
/**
 * The collection: its heading, its collection-wide controls and its table.
 *
 * `data-document-collection` is how a browser test addresses one without reaching into an emotion class
 * name, `data-document-collection-header` the heading strip inside it, and
 * `data-document-collection-controls` its collection-wide controls in the toolbar (`proposalDocumentList`
 * / `proposalDocumentListControls` in `e2e/support/proposal-ui.ts`). Deliberately not `section` as a
 * selector — the proposal page's own layout already uses several of those above this one.
 *
 * The hidden file input a collection uploads through is a FOLLOWING SIBLING of this element on both
 * pages, because that is how `proposalDocumentUploadInput` reaches it.
 */
export const DocumentCollectionRoot = styled("section")(() => ({
  display: "flex",
  flexDirection: "column",
  minHeight: 0,
  minWidth: 0,
  overflow: "hidden",
  position: "relative",
  width: "100%",
}));

/**
 * The heading strip. Its title is prose, so it takes the prose inset while the table beneath it runs to
 * the tab's edges — the draft's heading used to sit on the resize divider, 16px left of the notice and
 * the switch above it and of the column names below. See `workPaneProseInset`.
 */
export const DocumentCollectionHeader = styled("div")(({ theme }) => ({
  ...workPaneProseInset(theme),
  alignItems: "center",
  color: theme.palette.common.black,
  display: "flex",
  fontSize: "1.3em",
  justifyContent: "space-between",
  minHeight: theme.spacing(4.5),
}));

/**
 * The collection's name.
 *
 * The title stays a direct text child, with any info tooltip beside it rather than around it: a wrapper
 * would take the words out of the heading's own text node, and the specs read the heading as
 * `getByText(title, { selector: "h3" })`.
 */
export const DocumentCollectionHeading = styled("h3")(({ theme }) => ({
  ...theme.typography.h6,
  color: theme.palette.common.black,
  fontSize: "inherit",
  fontWeight: theme.typography.fontWeightRegular,
  lineHeight: 1.4,
  margin: 0,
}));

/** The row's trailing controls, packed against the pinned column's edge. */
export const DocumentRowActions = styled("div")(() => ({
  alignItems: "center",
  display: "flex",
  justifyContent: "flex-end",
  whiteSpace: "nowrap",
}));

/** An icon control in a row or in a heading strip. */
export const DocumentCollectionActionButton = styled(IconButton)(({ theme }) => ({
  padding: theme.spacing(0.5),
}));

/** The lock glyph's cell, centred on the row it locks. */
export const DocumentLockCellContent = styled("span")({
  alignItems: "center",
  display: "inline-flex",
  justifyContent: "center",
  lineHeight: 0,
  verticalAlign: "middle",
  width: "100%",
});
// #endregion

// #region Toolbar
/**
 * A collection's whole toolbar on one wrapping row: its search box, the ways it can be added to, and
 * the three view buttons at the far end.
 *
 * ONE site, not two. It used to be drawn at whichever of MRT's two toolbar slots suited the pane, so
 * that a narrow pane did not lose the search row's width to a cell sized for the buttons; the row
 * wraps now, which answers the same question from the real width without the collection having to be
 * told how narrow it is. `renderToolbarInternalActions` returns an empty fragment beside this — MRT
 * reads that slot's result with `??`, so a nullish return falls back to its own default buttons and
 * the page ends up with two sets.
 */
export function DocumentCollectionToolbar<TData extends MRT_RowData>(
  props: Readonly<{
    table: MRT_TableInstance<TData>;
    addActions?: readonly TableAddAction[];
    /**
     * Controls about the whole collection, such as its change history. They lead the view buttons, on
     * their row and grid: on a heading row of their own they lined up with nothing (#6035).
     */
    collectionActions?: ReactNode;
  }>,
): ReactElement {
  // MRT's table instance is one mutable object, the same prop on every render, so a memoized element
  // drawn with it never re-renders: the column list kept the switches it first drew (#6599).
  "use no memo";

  const { table, addActions, collectionActions } = props;
  return (
    <CollectionToolbar>
      <CollectionToolbarSearch>
        <GlobalFilterTextField table={table} />
      </CollectionToolbarSearch>
      <TableAddActions actions={addActions} />
      <CollectionToolbarViewButtons data-document-collection-controls="">
        {collectionActions}
        <TableToolsMenu table={table} />
      </CollectionToolbarViewButtons>
    </CollectionToolbar>
  );
}
// #endregion

// #region Empty state
const NoRowsContainer = styled("div")<{ maxVisibleWidth: number }>(({ maxVisibleWidth, theme }) => ({
  alignItems: "center",
  display: "flex",
  justifyContent: "center",
  maxWidth: `min(100vw, ${maxVisibleWidth}px)`,
  padding: theme.spacing(5, 0),
  width: "100%",
}));

/**
 * What a collection with no rows says, sized to the paper rather than to the table it stands in for.
 *
 * The wording changes while a filter is running: "No Documents" is a claim about the collection, and it
 * would be a false one about a collection whose documents simply do not match what was typed.
 */
export function DocumentCollectionNoRows<TData extends MRT_RowData>(
  props: Readonly<{ table: MRT_TableInstance<TData>; filtered: boolean }>,
) {
  const { table, filtered } = props;
  return (
    <NoRowsContainer maxVisibleWidth={table.refs.tablePaperRef.current?.clientWidth ?? 360}>
      <Typography color="text.secondary" sx={{ fontWeight: 500 }} variant="h6">
        {filtered ? "No matching documents" : "No Documents"}
      </Typography>
    </NoRowsContainer>
  );
}
// #endregion

// #region Cell treatment
/**
 * Makes the pinned controls column opaque.
 *
 * A collection is drawn in a panel narrower than five columns, so the Added column routinely scrolls
 * underneath the pinned controls. MRT paints a pinned cell's backdrop as a `::before` at 0.97 alpha and
 * gives the whole cell `opacity: 0.97`, which left the date legible *through* the icons — the columns
 * read as printed on top of one another rather than as one scrolled behind the other. Fully opaque, the
 * inset shadow MRT already draws down the pinned column's leading edge reads as what it is: an edge the
 * rows continue past.
 */
function opaquePinnedColumnStyles(theme: Theme) {
  return {
    opacity: 1,
    '&[data-pinned="true"]': {
      "&:before": { backgroundColor: theme.palette.background.paper },
    },
  };
}

/**
 * A body cell's own treatment: the Fee Schedule's body2 scale, an opaque pinned controls column, and
 * tighter padding where the screen is narrow. Whatever a page draws ACROSS its rows goes on top of this.
 */
export function documentCollectionBodyCellStyles(theme: Theme, columnId: string) {
  // Hoisted rather than written inline as `[theme.breakpoints.down("md")]`: a call expression as a
  // computed key is one of the forms the React Compiler cannot lower, and callers spread this result
  // inside their own components (see app/CLAUDE.md -> React Compiler).
  const narrowScreen = theme.breakpoints.down("md");
  return {
    ...feeScheduleTableCellStyles(theme, { columnId }),
    ...(columnId === ROW_ACTIONS_COLUMN_ID && opaquePinnedColumnStyles(theme)),
    verticalAlign: "middle",
    [narrowScreen]: {
      padding: theme.spacing(0.5, 1),
    },
  };
}

/**
 * The matching head cell: opaque over the pinned column, and never wrapping a column's name.
 *
 * The controls column takes its own head and body cell props from `displayColumnDefOptions` below,
 * which replace rather than extend these — the opacity is restated there, and this branch is what a
 * caller gets if it ever draws the column without those. Nothing here sets a width: every column's
 * width comes from MRT's `columnSizing` state, which the fit fills in.
 */
export function documentCollectionHeadCellStyles(theme: Theme, columnId: string) {
  return columnId === ROW_ACTIONS_COLUMN_ID
    ? opaquePinnedColumnStyles(theme)
    : { whiteSpace: "nowrap" as const };
}

export { FeeScheduleTableCellText } from "_shared/fee-schedules/categories/_views/fee-schedule-table-cell";
// #endregion

// #region Column fit
/**
 * What MRT is told about which columns exist and how wide they are, once the collection is measured.
 *
 * `columnVisibility` is the reader's own Show/Hide columns choices and nothing else — the fit never
 * hides a column, it sizes them and lets the table overflow. `columnSizing` is MRT's own per-column
 * width state, which is what the table's CSS variables, and therefore every head and body cell's
 * `width`, are built from.
 */
export type DocumentCollectionColumnFitState = {
  columnVisibility: Record<string, boolean>;
  onColumnVisibilityChange: (
    updaterOrValue: Record<string, boolean> | ((previous: Record<string, boolean>) => Record<string, boolean>),
  ) => void;
  columnSizing: Record<string, number>;
  /**
   * How a column the reader dragged, or double-clicked open, reaches the table's sizing state.
   *
   * The fit is what the columns start at and what they go back to; a width the reader chose sits on
   * top of it until they double-click that column again, which removes the entry rather than storing
   * one and so hands the column back to the fit.
   */
  onColumnSizingChange: (
    updaterOrValue: MRT_ColumnSizingState | ((previous: MRT_ColumnSizingState) => MRT_ColumnSizingState),
  ) => void;
  /** Draft rows and active rows offer different numbers of controls; see `document-collection-columns`. */
  rowActionsWidth: number;
};

/**
 * Fits a collection's columns into the room the panel actually gives it.
 *
 * The width is measured off the collection's own element rather than off the window or the proposal
 * pane: a collection sits inside a tab inside a pane inside the window, and every one of those takes a
 * share before the table sees what is left. `useMeasuredWidth` is the source rather than the table's
 * own scroll container, because that container reports a
 * `clientWidth` of 0 wherever there is no layout — jsdom, and any renderer without `ResizeObserver` —
 * and a width of 0 would size every column down to its floor for no reason. An unmeasured width has to
 * be `undefined`, so that nothing is sized at all and the columns keep the sizes they were declared
 * with.
 *
 * Handed back as a tuple of [ref, fit] the way `useMeasuredWidth` hands back its own, so the caller
 * writes `ref={collectionRef}` rather than `ref={fit.collectionRef}`: `react-hooks/refs` reads a ref
 * taken off an object property in JSX as a ref being *read* during the render, and an eslint-disable
 * for any `react-hooks/*` rule costs the whole component its React Compiler memoization.
 */
export function useDocumentCollectionColumnFit(
  params: Readonly<{
    showLockColumn: boolean;
    rowActionsWidth: number;
    /**
     * Remembers the reader's Show/Hide columns choice in this browser under this key (#2788). Left out
     * by a collection drawn inside a picker, where the choice lasts as long as the picker.
     */
    persistColumnVisibilityAs?: string;
  }>,
): [(node: HTMLElement | null) => void, DocumentCollectionColumnFitState] {
  const { showLockColumn, rowActionsWidth, persistColumnVisibilityAs } = params;
  const [collectionRef, collectionWidth] = useMeasuredWidth();
  // What the reader chose in the Show/Hide columns menu, which is the only thing that ever hides a
  // column here. The fit sizes columns and lets the table overflow; a reader who would rather see fewer
  // columns than scroll says so through that menu, and their choice is the whole of this state.
  const [chosenColumnVisibility, setChosenColumnVisibility] = usePersistedColumnVisibility(
    persistColumnVisibilityAs,
  );
  const [chosenColumnSizing, setChosenColumnSizing] = useState<MRT_ColumnSizingState>({});

  const columnFit = useMemo<DocumentCollectionColumnFit>(() => {
    const presentColumnIds = [
      ...(showLockColumn ? [LOCKED_COLUMN_ID] : []),
      ...DOCUMENT_CONTENT_COLUMN_IDS,
      ROW_ACTIONS_COLUMN_ID,
    ];
    return fitDocumentCollectionColumns(collectionWidth, presentColumnIds, rowActionsWidth);
  }, [collectionWidth, rowActionsWidth, showLockColumn]);

  /**
   * Records the widths the reader chose for themselves, over the top of the fit.
   *
   * A column with no entry here is one the fit still sizes, which is what makes a second double-click
   * on a header — which removes the entry — mean "back to the width this collection gives it".
   */
  const handleColumnSizingChange = useCallback(
    (
      updaterOrValue: MRT_ColumnSizingState | ((previous: MRT_ColumnSizingState) => MRT_ColumnSizingState),
    ) => {
      setChosenColumnSizing((chosen) =>
        typeof updaterOrValue === "function" ? updaterOrValue(chosen) : updaterOrValue,
      );
    },
    [],
  );

  const columnSizing = useMemo(
    () =>
      Object.entries(chosenColumnSizing).reduce(
        (sizing, [columnId, width]) => applyColumnWidth(sizing, columnId, width),
        columnFit.widths,
      ),
    [chosenColumnSizing, columnFit.widths],
  );

  return [
    collectionRef,
    {
      columnVisibility: chosenColumnVisibility,
      onColumnVisibilityChange: setChosenColumnVisibility,
      columnSizing,
      onColumnSizingChange: handleColumnSizingChange,
      rowActionsWidth,
    },
  ];
}
// #endregion

// #region Table options
export type DocumentCollectionTableOptionParams = {
  /** The table's accessible name — the collection's own heading, so a test can find one by name. */
  tableName: string;
  query: string;
  onQueryChange: (query: string) => void;
  /** True in the pane sizes where a comfortable row is too tall to show a collection whole. */
  compactDensity: boolean;
  tableContainerRef: RefObject<HTMLDivElement | null>;
  /** What `useDocumentCollectionColumnFit` made of the room the collection was given. */
  columnFit: DocumentCollectionColumnFitState;
};

/**
 * Everything both collections ask MRT for, leaving each page its own columns, rows and cells.
 *
 * Spread FIRST into a caller's option literal, so the caller's `columns` and `data` win.
 */
export function documentCollectionTableOptions<TData extends MRT_RowData>(
  params: DocumentCollectionTableOptionParams,
): Partial<MRT_TableOptions<TData>> {
  const { tableName, query, onQueryChange, compactDensity, tableContainerRef, columnFit } = params;
  const rowActionsWidth = columnFit.rowActionsWidth;
  const fittedRowActionsWidth = columnFit.columnSizing[ROW_ACTIONS_COLUMN_ID] ?? rowActionsWidth;
  return {
    // Resizable columns, and the `grid` layout they need. The fit below still decides what each column
    // STARTS at; `grid` is what makes a drag on a column's edge move it, which a semantic table's own
    // sizing would simply overrule. See `table-column-widths`.
    ...resizableColumnOptions<TData>(),
    enableRowActions: true,
    // `enableColumnActions` is deliberately unset: MRT's default is on, and the Fee Schedule beside
    // these tables simply omits it too. A column's `⋮` menu opens only because a reader clicked it, and
    // closes on the next click anywhere — an ordinary transient dialog, not the self-opening popover
    // #4895 was about.
    enableColumnPinning: true,
    enableColumnFilters: true,
    enableGlobalFilter: true,
    enableGlobalFilterRankedResults: false,
    globalFilterFn: "includesString",
    enableKeyboardShortcuts: false,
    enableStickyHeader: true,
    // Sorting is on, client-side, and `manualSorting` stays unset so that MRT actually reorders the
    // rows on the accessor values rather than only moving the header arrow. A `⋮ → Sort by …` that
    // does nothing is worse than no sort control at all.
    //
    // A document collection is a handful of rows and is always shown whole, so there is no paginator
    // and no bottom toolbar beneath three documents.
    enablePagination: false,
    enableBottomToolbar: false,
    enableTableFooter: false,
    manualPagination: true,
    onGlobalFilterChange: onQueryChange,
    positionGlobalFilter: "none",
    onColumnVisibilityChange: columnFit.onColumnVisibilityChange,
    onColumnSizingChange: columnFit.onColumnSizingChange,
    // `columnSizing` is how the fit reaches the cells: MRT builds a `--header-<id>-size` CSS variable
    // per column out of it, and every head and body cell's `width` is that variable. Empty until the
    // collection has been measured, which leaves each column on the `size` its definition carries.
    // A width the reader dragged, or double-clicked open, is layered over it by the fit itself.
    state: {
      columnSizing: columnFit.columnSizing,
      columnVisibility: columnFit.columnVisibility,
      globalFilter: query,
    },
    initialState: {
      columnPinning: { right: [ROW_ACTIONS_COLUMN_ID] },
      density: compactDensity ? "compact" : "comfortable",
      // The per-column filter row is offered, not imposed: Toggle filters opens it.
      showColumnFilters: false,
      showGlobalFilter: true,
    },
    /**
     * Opts the controls column out of the shared shrink-to-content rule in `MRTNoMemo`. That rule
     * declares a 1% width so a browser's AUTO table layout gives the column its content width and the
     * slack to the data columns — but this table lays its columns out from the widths it measured, and
     * 1% of the panel would collapse the column with its buttons hanging outside it. Here the width is
     * the one `document-collection-columns` measured the row's controls against.
     *
     * It takes no share of a wide panel's slack and offers no resize handle: it is sized to the
     * controls a row can carry, and dragging it wider would only take width off the document's own
     * columns.
     *
     * Column-level props rather than the table-level callback, because that is the only level that
     * replaces `MRTNoMemo`'s `sx` rather than being overwritten by it — so the pinned column's opacity
     * has to be restated here, since `documentCollectionBodyCellStyles` no longer reaches this column.
     */
    displayColumnDefOptions: {
      [ROW_ACTIONS_COLUMN_ID]: {
        size: rowActionsWidth,
        minSize: rowActionsWidth,
        grow: false,
        enableResizing: false,
        muiTableHeadCellProps: {
          align: "center" as const,
          sx: (theme: Theme) => ({
            px: 0.5,
            minWidth: rowActionsWidth,
            width: fittedRowActionsWidth,
            ...opaquePinnedColumnStyles(theme),
          }),
        },
        muiTableBodyCellProps: {
          align: "center" as const,
          sx: (theme: Theme) => ({
            px: 0.5,
            minWidth: rowActionsWidth,
            width: fittedRowActionsWidth,
            verticalAlign: "middle",
            // `align` alone cannot reach the controls: the row's container is a block-level flex box,
            // so it fills the cell and packs its buttons at the start whatever the cell says.
            "& > *": { justifyContent: "center" },
            ...opaquePinnedColumnStyles(theme),
          }),
        },
      },
    },
    // The widths above are the widths the columns get, which is what gives the `nowrap` truncation the
    // cells already ask for something to truncate against. Under a browser's own AUTO sizing a column
    // is as wide as its longest cell, and a `nowrap` cell's minimum content width equals its maximum —
    // so the truncation could never be granted and the table grew past the panel instead, with the
    // Added column scrolling under the pinned controls (#4955). `grid` is what holds them now, and it
    // is also what lets a reader drag one wider.
    muiTableProps: { "aria-label": tableName, sx: { borderSpacing: 0 } },
    // MRT lays the top toolbar out as a grid whose inner flex Box keeps `min-width: auto`, so the track
    // sizes itself to that Box's CONTENT — and the toolbar, being `overflow: hidden`, then clips
    // whatever ends up past the right edge with no scrollbar to say so. A zero floor lets the track
    // clamp to the panel instead, which is what the toolbar's one wrapping row needs in order to wrap.
    //
    // The search and the add controls are prose, so they take the same inset as the heading above them.
    // That inner Box pads itself by 0.5rem as well, which would stand the search box 8px inside the
    // heading's line, so its leading padding goes and the toolbar's inset is the whole of it. A
    // callback rather than a theme parameter, so a caller's memo does not have to depend on the theme.
    muiTopToolbarProps: {
      sx: (toolbarTheme: Theme) => ({
        ...workPaneProseInset(toolbarTheme),
        "& > .MuiBox-root": { minWidth: 0, paddingLeft: 0 },
      }),
    },
    // No `backgroundColor: transparent` here. The row-actions column is pinned, and MRT draws a
    // pinned cell's background by inheriting the paper's — so a transparent paper left the controls
    // column see-through and the Added column underneath it bled through the icons.
    muiTablePaperProps: { ...paneStretchTable, elevation: 0 },
    // `overflowX: auto` is what makes the columns past the pinned controls reachable at all; the
    // scrollbar `MRTNoMemo` styles on this container is what says they are there, and the ref is what
    // a double-click on a header measures that column's content in.
    muiTableContainerProps: {
      ref: tableContainerRef,
      sx: { minWidth: 0, overflowX: "auto", position: "relative" },
    },
    muiSearchTextFieldProps: {
      placeholder: "Search",
      size: "small",
      variant: "outlined",
      slotProps: {
        input: {
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
          endAdornment: query ? (
            <InputAdornment position="end">
              <IconButton aria-label="Clear search" size="small" onClick={() => onQueryChange("")}>
                <CloseIcon />
              </IconButton>
            </InputAdornment>
          ) : undefined,
        },
      },
    },
  };
}
// #endregion
